import { DEPRECATED_UPLOAD_SCREENSHARE_ID, SCREENSHARE_VIDEO_ID } from '../constants/screenshare';
import { ContentMetadataStoreFactoryType } from '../stores/contentMetadataStoreFactory';
import type { Clip } from '../types/clip-types';
import {
  canUseNewTimelineForOldLiveEvent,
  canUseNewTimelineForUnmigratedLiveEvent,
  doesClipHaveScenes,
  getClipReadOnlyPlayerConfig,
  isLayoutWithVisionDataEnabledPure,
  isLiveEventRemotionWithPreviewAndAlignedStream
} from './clipUtils';
import { isSpeakerLayout } from './layout';
import { isLiveEventWithManualMigration } from './mediaContentUtils';
import { getCompositeSegment } from './playerConfigGetters';
import { rectifyScenes } from './sceneUtils';
import { CLIP_CUSTOMIZER_LAYOUT_TYPES } from '../constants/layouts.constants';

export const migrateClip = ({
  clip,
  isSharedPage,
  contentMetadataStoreInstance,
  isFullFaceRecEnabled,
  areLayoutsEnabledForPreRecordedEvents,
  logEvent,
  infoToast
}: {
  clip: Clip;
  isSharedPage: boolean;
  contentMetadataStoreInstance: ContentMetadataStoreFactoryType;
  isFullFaceRecEnabled: boolean;
  areLayoutsEnabledForPreRecordedEvents: boolean;
  logEvent: (clip: Clip, message: string) => void;
  infoToast: (message: string) => void;
}) => {
  if (isSharedPage) {
    return { hasMigratedClip: false, clip };
  }
  const speakerLayoutIsNotSupported =
    canUseNewTimelineForOldLiveEvent({ clip }) || canUseNewTimelineForUnmigratedLiveEvent(clip);
  if (speakerLayoutIsNotSupported && isSpeakerLayout(clip.asset_metadata.layout)) {
    const oldLayout = clip.asset_metadata.layout;
    const oldLayoutHumanReadableName = CLIP_CUSTOMIZER_LAYOUT_TYPES.find(layout => layout.value === oldLayout)?.label;
    infoToast(`We've changed your clip layout from ${oldLayoutHumanReadableName} to Original.`);
    logEvent(clip, 'CLIP_MIGRATED_TO_ORIGINAL_LAYOUT');
    clip.asset_metadata.layout = 'DEFAULT';
    if (doesClipHaveScenes(clip)) {
      // Ideally we will never go here, because scenes won't exist until new timeline is mounted
      logEvent(clip, 'FOUND_SCENES_IN_CLIP_WILL_BE_DISCARDED');
      clip.asset_metadata.scenes = undefined;
    }
    return { hasMigratedClip: true, clip };
  }
  if (
    isSpeakerLayout(clip.asset_metadata.layout) &&
    isLayoutWithVisionDataEnabledPure({ clip, areLayoutsEnabledForPreRecordedEvents, isFullFaceRecEnabled })
  ) {
    const layoutKey = (
      clip.asset_metadata.layout === 'FOCUS' ? 'GRID' : clip.asset_metadata.layout
    ) as keyof typeof clip.layout_status;
    const layoutStatusInfo = clip.layout_status[layoutKey];
    if (layoutStatusInfo && layoutStatusInfo.accurate !== true) {
      clip.asset_metadata.layout = 'DEFAULT';
      clip.asset_metadata.scenes = undefined;
      return { hasMigratedClip: true, clip };
    }
  }
  if (isLiveEventRemotionWithPreviewAndAlignedStream(clip) && isLiveEventWithManualMigration(clip.content)) {
    // Old event migration complete
    const playerConfig = getClipReadOnlyPlayerConfig(clip);
    if (!playerConfig) {
      if (doesClipHaveScenes(clip)) {
        clip.asset_metadata.scenes = undefined;
        logEvent(clip, 'LIVE_EVENT_AFTER_MIGRATION_SCENES_EXISTS_WITHOUT_NEW_TIMELINE');
        return { hasMigratedClip: true, clip };
      }
      logEvent(clip, 'LIVE_EVENT_AFTER_MIGRATION_NO_PLAYER_CONFIG');
      return { hasMigratedClip: true, clip };
    }
    const compositeSegment = getCompositeSegment(playerConfig);
    if (!compositeSegment) {
      throw new Error('Player config exists without composite segment');
    }
    const originalExists = compositeSegment.videos.some(v => v.isOriginalVideo);
    if (!originalExists) {
      logEvent(clip, 'LIVE_EVENT_AFTER_MIGRATION_ORIGINAL_NOT_FOUND');
      if (doesClipHaveScenes(clip)) {
        logEvent(clip, 'LIVE_EVENT_AFTER_MIGRATION_ORIGINAL_NOT_FOUND_SCENES_DISCARDED');
        // need to do this because video crop might have existed on basis of video ids in scenes
        clip.asset_metadata.scenes = undefined;
      }
      return { hasMigratedClip: true, clip };
    }
    const segmentVideoUri = compositeSegment.videos.map(v => v.details.src);
    const uniqueVideoUris = [...new Set(segmentVideoUri)];
    if (uniqueVideoUris.length === 1 && segmentVideoUri.length > 1) {
      logEvent(clip, 'LIVE_EVENT_AFTER_MIGRATION_ALL_VIDEOS_POINTING_TO_SAME_SOURCE');
      // all videos were pointing to same original source, migrate to start using individual recording source
      // ⚠️ can get executed repeatedly if 0 individual recordings/speakers
      if (doesClipHaveScenes(clip)) {
        logEvent(clip, 'LIVE_EVENT_AFTER_MIGRATION_ALL_VIDEOS_POINTING_TO_SAME_SOURCE_SCENES_DISCARDED');
        // need to do this because video crop might have existed on basis of video ids in scenes
        clip.asset_metadata.scenes = undefined;
      }
      return { hasMigratedClip: true, clip };
    }
    logEvent(clip, 'LIVE_EVENT_NO_NEED_TO_RE_GRENERATE_PLAYER_CONFIG');
  }
  if (doesClipHaveScenes(clip)) {
    //Remove this screenshare migration logic after a few months when majority of the clips(31 recordings) would have been migrated, as this is to fix the issue of screenshare video id change for custom upload clips (4May/2026)
    let hasScreenshareIdMigration = false;
    const scenesWithMigratedScreenshareId = clip.asset_metadata.scenes!.map(scene => {
      const migratedScene = structuredClone(scene);
      const replaceId = (id: string) => {
        if (id === DEPRECATED_UPLOAD_SCREENSHARE_ID) {
          hasScreenshareIdMigration = true;
          return SCREENSHARE_VIDEO_ID;
        }
        return id;
      };

      migratedScene.details.all_video_ids = migratedScene.details.all_video_ids.map(replaceId);
      migratedScene.details.visible_video_ids = migratedScene.details.visible_video_ids.map(replaceId);

      if (migratedScene.details.video_crop_position_map?.[DEPRECATED_UPLOAD_SCREENSHARE_ID]) {
        migratedScene.details.video_crop_position_map[SCREENSHARE_VIDEO_ID] =
          migratedScene.details.video_crop_position_map[DEPRECATED_UPLOAD_SCREENSHARE_ID];
        delete migratedScene.details.video_crop_position_map[DEPRECATED_UPLOAD_SCREENSHARE_ID];
        hasScreenshareIdMigration = true;
      }

      return migratedScene;
    });
    if (hasScreenshareIdMigration) {
      logEvent(clip, 'SCREENSHARE_VIDEO_ID_MIGRATED');
      clip.asset_metadata.scenes = scenesWithMigratedScreenshareId;
    }

    const { updatedScenes, rectified } = rectifyScenes({
      clip,
      contentMetadataStoreInstance,
      isFullFaceRecEnabled,
      areLayoutsEnabledForPreRecordedEvents
    });
    if (rectified) {
      logEvent(clip, 'SCENES_RECTIFIED');
      clip.asset_metadata.scenes = updatedScenes;
    }

    if (hasScreenshareIdMigration || rectified) {
      return { hasMigratedClip: true, clip };
    }
  }
  return { hasMigratedClip: false, clip };
};
